import { type FC, useState } from 'react';
import { graphql, usePaginationFragment } from 'react-relay';
import { FormattedMessage } from 'dibs-react-intl';
import { Button } from 'dibs-elements/exports/Button.tsx';
import { NftCreatorTile } from './NftCreatorTile/NftCreatorTile.tsx';
import { useDeviceType } from '../../../nftHeader/hooks/useDeviceType.tsx';

import { type NftCreatorTiles_viewer$key } from './__generated__/NftCreatorTiles_viewer.graphql.ts';
import { type NftCreatorTilesPaginationQuery } from './__generated__/NftCreatorTilesPaginationQuery.graphql.ts';

import classnames from 'classnames';
import styles from './NftCreatorTiles.scss';

type Props = {
    viewer: NftCreatorTiles_viewer$key | null | undefined;
    isSearch: boolean;
};

export const NftCreatorTiles: FC<Props> = ({ isSearch, viewer: viewerRef }) => {
    const { isMobile, deviceType } = useDeviceType();
    const isTablet = deviceType === 'tablet';
    const isDesktop = deviceType === 'desktop';
    const [viewMoreMobile, setViewMoreMobile] = useState(true);
    const LOAD_NEXT_VALUE = isMobile ? 4 : 80;
    const { data, hasNext, loadNext, isLoadingNext } = usePaginationFragment<
        NftCreatorTilesPaginationQuery,
        NftCreatorTiles_viewer$key
    >(
        graphql`
            fragment NftCreatorTiles_viewer on Viewer
            @argumentDefinitions(
                creatorsItemCount: { type: "Int", defaultValue: 80 }
                after: { type: "String" }
                search: { type: "String", defaultValue: "" }
            )
            @refetchable(queryName: "NftCreatorTilesPaginationQuery") {
                nftCreators(
                    first: $creatorsItemCount
                    after: $after
                    search: $search
                    publicProfileUsername: "*"
                ) @connection(key: "NftCreatorTiles_nftCreators") {
                    totalResults
                    edges {
                        node {
                            ...NftCreatorTile_nftCreator
                        }
                    }
                }
            }
        `,
        viewerRef
    );
    const creators = data?.nftCreators?.edges?.map((creator, index) => {
        return (
            <NftCreatorTile
                nftCreator={creator?.node || null}
                key={`creator-${index}`}
                isSearch={isSearch}
            />
        );
    });

    let showNext = hasNext;
    if (hasNext && isMobile && isSearch && !viewMoreMobile) {
        showNext = false;
    } else if (hasNext && isTablet && isSearch) {
        showNext = false;
    } else if (hasNext && isDesktop && isSearch) {
        showNext = false;
    }

    const containerClasses = classnames(styles.container, {
        [styles.isSearch]: isSearch,
    });

    const gridClasses = classnames(styles.grid, {
        [styles.isSearch]: isSearch,
    });

    const viewMoreContainerClasses = classnames(styles.viewMoreContainer, {
        [styles.isSearch]: isSearch,
    });

    const viewMoreButtonClasses = classnames(styles.viewMoreButton, {
        [styles.isSearch]: isSearch,
    });

    return (
        <div className={containerClasses}>
            <ul className={gridClasses}>{creators}</ul>
            <div className={viewMoreContainerClasses}>
                {showNext && (
                    <Button
                        type="secondary"
                        size="medium"
                        disabled={isLoadingNext}
                        onClick={() => {
                            loadNext(LOAD_NEXT_VALUE);
                            if (isMobile) {
                                setViewMoreMobile(false);
                            }
                        }}
                        className={viewMoreButtonClasses}
                    >
                        <FormattedMessage
                            id="nft.landing.creators.viewMore"
                            defaultMessage="View More"
                        />
                    </Button>
                )}
            </div>
        </div>
    );
};
